import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PlanDispatchDialog, freeQty } from "@/components/planning/PlanDispatchDialog";

const isOpen = (p) => !["closed", "cancelled"].includes(p.status) && p.movement_status !== "fully_assigned";
export const canSubmitQc = (p) => isOpen(p) && p.status !== "draft" && !p.change_hold && ["not_submitted", "rejected"].includes(p.qc_status);

function PlanPicks({ plans, sel, setSel }) {
  const flip = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <div className="space-y-2" data-testid="rfq-plan-list">
      {plans.map((p) => {
        const ok = canSubmitQc(p);
        return (
          <label key={p.id} className={`flex items-start gap-3 rounded border px-3 py-2 text-xs ${ok ? "cursor-pointer" : "opacity-60"} ${sel.includes(p.id) ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}>
            <input type="checkbox" className="mt-0.5" disabled={!ok} checked={sel.includes(p.id)} onChange={() => flip(p.id)} data-testid={`rfq-pick-${p.planned_dispatch_number}`} />
            <span className="flex-1">
              <span className="flex flex-wrap items-center gap-2 font-semibold"><span className="font-mono">{p.planned_dispatch_number}</span><span className="font-normal text-slate-500">{fmtDate(p.expected_dispatch_date)}</span><StatusBadge status={p.qc_status} /><StatusBadge status={p.status} /></span>
              <span className="block text-slate-600">{p.item_summary}</span>
              {p.qc?.qc_remarks && <span className="block text-amber-800">Last QC remark: {p.qc.qc_remarks}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
}

// Production -> QC handoff: submit open planned dispatches, or plan a Part / Full dispatch that goes straight to QC
export function ReadyForQcDialog({ order, preselect = [], onClose, onDone }) {
  const { data } = useFetch(`/planned-dispatches?lm_id=${order.id}`);
  const [sel, setSel] = useState(preselect);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);
  if (!data) return null;
  const open = data.filter(isOpen).filter(canSubmitQc);
  if (mode) return <PlanDispatchDialog order={order} mode={mode} free={freeQty(order, data)} onClose={() => setMode(null)} onSaved={onDone} />;
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/qc-submissions", { lm_id: order.id, qc_scope_type: "PlannedDispatchSelection", planned_dispatch_ids: sel, remarks });
      toast.success(`${sel.length} planned dispatch(es) submitted as Ready for QC`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="ready-for-qc-dialog">
        <DialogHeader><DialogTitle>Ready for QC · {order.order_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Plan what production has finished: Part Dispatch for some quantity, Full Dispatch for everything still free. The plan is sent to QC straight away.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setMode("part")} data-testid="rfq-part-dispatch-btn">Part Dispatch</Button>
          <Button variant="outline" onClick={() => setMode("full")} data-testid="rfq-full-dispatch-btn">Full Dispatch</Button>
        </div>
        {open.length > 0 && <>
          <p className="text-xs text-slate-500">Or tick existing planned dispatches production has finished. QC must approve them before logistics can move them.</p>
          <PlanPicks plans={open} sel={sel} setSel={setSel} />
          <div><Label>Remarks for QC</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="rfq-remarks" /></div>
        </>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="rfq-cancel-btn">Cancel</Button>
          {open.length > 0 && <Button disabled={busy || !sel.length} onClick={go} data-testid="rfq-submit-btn">{`Submit ${sel.length || ""} as Ready for QC`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
